import React, { useEffect, useState, useRef } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Button } from "react-bootstrap";
import {
  getLogisticsPackageByIdParams,
  putLogisticsSaveMapParams,
  putLogisticsDisableParams,
  putLogisticsChangeStatus,
  duplicateLinesToNewReceip,
} from "../../../../../Backend/LogisticsRequests";
import BasicTable from "../../../../../Components/BasicTable/BasicTable";
import {
  bookNumberMask,
  displayDateTime,
  getRandomNumber,
  parseDateFromUtc,
} from "../../../../../Helpers/utils/utils";
import { hasValue } from "../../../../../Helpers/validations/common";
import { LinkToBarcodeModal } from "../LinkToBarcodeModal/LinkToBarcodeModal";
import "./LogisticsPackage.scss";
import { CompleteModal } from "./CompleteModal/CompleteModal";
import { DisableReceipModal } from "./DisableReceipModal/DisableReceipModal";
import { DuplicateModal } from "./DuplicateModal/DuplicateModal";
import LogisticsPackageExtra, {
  initLine,
  initLogisticsPackage,
} from "./LogisticsPackageExtra";
import { LinkToReturnModal } from "../LinkToReturnModal/LinkToReturnModal";
import { LinkToBookModal } from "../LinkToBookModal/LinkToBookModal";
import ReturnLinesTable from "../../../../../Components/ReturnLinesTable";
import { ChangeReturnStatusModal } from "./ChangeReturnStatusModal/ChangeReturnStatusModal";

export function LogisticsPackage() {
  const navigate = useNavigate();
  const [t] = useTranslation();
  const { id } = useParams();
  const tableLinesRef = useRef(null);
  const tableBarcodeRef = useRef(null);

  //#region States
  const [logisticsPackage, setLogisticsPackage] =
    useState(initLogisticsPackage);
  const [showDuplicateModal, setShowDuplicateModal] = useState(false);
  const [showCompleteModal, setShowCompleteModal] = useState(false);
  const [showDisableModal, setShowDisableModal] = useState(false);
  const [showChangeReturnStatusModal, setShowChangeReturnStatusModal] = useState(false);
  const [showLinkToBarcodeModal, setShowLinkToBarcodeModal] = useState(false);
  const [showLinkToReturnModal, setShowLinkToReturnModal] = useState(false);
  const [showLinkToBookModal, setShowLinkToBookModal] = useState(false);
  const [activeLine, setActiveLine] = useState(initLine);
  const [viewMoreInfoBarcode, setViewMoreInfoBarcode] = useState(false);
  const [viewMoreInfoLog, setViewMoreInfoLog] = useState(false);
  const [showWarning, setShowWarning] = useState(true);
  const [linesColumns, setLinesColumns] = useState([]);
  const [barcodeInfo, setBarcodeInfo] = useState();
  const [loading, setLoading] = useState(false);
  const [barcodeColumns] = useState([
    {
      name: "number",
      headerCaption: "#",
      size: "tiny",
      caption: (x, i) => i + 1,
    },
    {
      name: "book.title",
      headerCaption: t("Title"),
      size: "large",
    },
    {
      name: "book.sku",
      headerCaption: t("SKU"),
      size: "small",
    },
    {
      name: "book.bookNumber",
      headerCaption: t("BookNumber"),
      size: "small",
      textAlign: "right",
      caption: (x) => bookNumberMask(x.book.bookNumber),
    },
    {
      name: "schoolScanDate",
      headerCaption: t("ScanDate"),
      size: "normal",
      textAlign: "right",
      caption: (x) => displayDateTime(parseDateFromUtc(x.schoolScanDate)),
    },
  ]);
  //#endregion

  const logisticsPackageExtraFuncs = LogisticsPackageExtra(logisticsPackage);

  const logisticsInfo = logisticsPackageExtraFuncs.getLogisticsInfo();

  const showLinesWarning =
    logisticsPackage.isManuallyMapped &&
    logisticsPackage.lines.filter((x) => !hasValue(x.barcodeBook)).length > 0;

  //#region Effect Hooks
  useEffect(() => {
    getLogisticsPackageById();
  }, []);

  useEffect(
    function updateBarcodeInfo() {
      setBarcodeInfo(
        hasValue(logisticsPackage.barcode)
          ? logisticsPackageExtraFuncs.getBarcodeInfo()
          : logisticsPackageExtraFuncs.getBarcodeReturnsInfo()
      );
    },
    [logisticsPackage]
  );

  //#endregion

  //#region Handlers
  async function handleCompleteModalComplete() {
    const resp = await putLogisticsSaveMapParams(
      id,
      !logisticsPackage.isMappedSaved
    );
    if (resp.success) {
      setShowCompleteModal(false);
      getLogisticsPackageById();
    }
  }

  async function handleDisableReceipModalDisable() {
    const resp = await putLogisticsDisableParams(
      id,
      !logisticsPackage.disabled
    );
    if (resp.success) {
      setShowDisableModal(false);
      getLogisticsPackageById();
    }
  }

  function handleChangeReturnStatusOnClick() {
    setShowChangeReturnStatusModal(true);
  }

  async function handleChangeReturnStatusOnConfirm() {
    const resp = await putLogisticsChangeStatus(id);
    if (resp.success) {
      navigate(-1);
    }
  }

  async function handleDuplicateModalDup() {
    const resp = await duplicateLinesToNewReceip(id);

    if (resp.success) {
      setShowDuplicateModal(false);
      getLogisticsPackageById();
      navigate(-1);
    }
  }

  async function handleLinkButtonClick() {
    if (hasValue(logisticsPackage.barcode)) {
      setShowLinkToBarcodeModal(true);
    }
    if (hasValue(logisticsPackage.barcodeReturn)) {
      setShowLinkToReturnModal(true);
    }
  }

  function handleLinkToNotReturnButtonClick() {
    setShowLinkToBarcodeModal(true);
  }

  function handleLinkToReturnButtonClick() {
    setShowLinkToReturnModal(true);
  }

  function handleLinkReturnModalSubmit() {
    getLogisticsPackageById();
    setShowLinkToReturnModal(false);
  }

  function handleLinkBarcodeModalSubmit() {
    getLogisticsPackageById();
    setShowLinkToBarcodeModal(false);
    navigate(-1);
  }

  function handleLinkToBookModalSubmit() {
    getLogisticsPackageById();
    setShowLinkToBookModal(false);
  }

  function handleOnLineOver(line) {
    let index;

    if (hasValue(line.barcodeBook)) {
      index = getBookFromPackage(line, logisticsPackage);
      if (index !== -1) {
        highlightBook(index);
      }
    }
    if (hasValue(line.barcodeReturnLine)) {
      index = getReturnLineFromPackage(line, logisticsPackage);
      if (index !== -1) {
        highlightReturnLine(index);
      }
    }
  }

  function handleOnLineOut(line) {
    let index;

    if (hasValue(line.barcodeBook)) {
      index = getBookFromPackage(line, logisticsPackage);
      if (index !== -1) {
        resetBook(index);
      }
    }
    if (hasValue(line.barcodeReturnLine)) {
      index = getReturnLineFromPackage(line, logisticsPackage)
      if (index !== -1) {
        resetReturnLine(index)
      }
    }
  }

  //#endregion

  //#region Functions
  function renderInfoRow(x, key) {
    return (
      <div className="info-row" key={"info-" + getRandomNumber() + "-" + key}>
        <div className="key">{x.key}:</div>
        <div className="value">{hasValue(x.value) ? x.value : "-"}</div>
      </div>
    );
  }

  function renderBarcodePackageInformation() {
    return (
      <div className="column">
        <div className="title-wrapper">
          <h2 className="title">{t("PackageInfo")}</h2>
          <div className="mapped-wrapper">
            <div
              title={
                logisticsPackage.isManuallyMapped
                  ? t("ThisPackageIsManuallyMapped")
                  : t("ThisPackageIsAutomaticallyMapped")
              }
              className={`mapped-type ${logisticsPackage.isManuallyMapped ? "manual" : "auto"
                }`}
            >
              {logisticsPackage.isManuallyMapped ? t("Manual") : t("Auto")}
            </div>
            {logisticsPackage.isManuallyMapped &&
              !logisticsPackage.isMappedSaved && (
                <Button
                  className="button"
                  onClick={() => handleLinkButtonClick()}
                >
                  {t("Edit")}
                </Button>
              )}
          </div>
        </div>

        <div className={`info-wrapper ${viewMoreInfoBarcode && `view-all`}`}>
          {barcodeInfo && barcodeInfo.map((x) => renderInfoRow(x, "logistics"))}
        </div>
        <div
          className="view-more-wrapper"
          onClick={() => setViewMoreInfoBarcode(!viewMoreInfoBarcode)}
        >
          <span className="icon">
            {viewMoreInfoBarcode ? "keyboard_arrow_up" : "keyboard_arrow_down"}
          </span>
          <span className="text">
            {viewMoreInfoBarcode ? t("ViewLess") : t("ViewMore")}
          </span>
        </div>
        <div className="books-wrapper">
          <div className="subtitle-wrapper">
            <h3 className="subtitle">
              {`${logisticsPackage.barcode ? t("Books") : t("ReturnLines")} 
              (${logisticsPackage.barcode
                  ? logisticsPackage.barcode?.barcodeBooks.length
                  : logisticsPackage.barcodeReturn
                    ? logisticsPackage.barcodeReturn?.lines.length
                    : 0
                })`}
            </h3>
          </div>
          {logisticsPackage.barcode ? (
            <BasicTable
              columns={barcodeColumns}
              ref={tableBarcodeRef}
              datasource={logisticsPackage.barcode?.barcodeBooks.sort(compare)}
            />
          ) : (
            <ReturnLinesTable sentLines={logisticsPackage.lines} dataSource={logisticsPackage.barcodeReturn?.lines.sort(
              compareLines
            )} />
          )}
        </div>
      </div>
    );
  }

  function getBookFromPackage(line, logiPackage) {
    return logiPackage.barcode.barcodeBooks.findIndex(
      (x) => x.book.id === line.barcodeBook.book.id
    );
  }

  function getReturnLineFromPackage(line, logiPackage) {
    return logiPackage.barcodeReturn.lines.findIndex(
      (x) => x.skus.some((sku) => sku.uid === line.sku)
    );
  }

  function getBookTarget(index) {
    const tableBooks = document.querySelector(".books-wrapper");
    const rows = tableBooks.querySelectorAll(".basic-table-row");
    return rows[index];
  }

  function getReturnLineTarget(index) {
    const rows = document.querySelectorAll(".return-lines-wrapper");
    return rows[index];
  }

  function highlightBook(index) {
    const targetRow = getBookTarget(index);
    targetRow.style.backgroundColor = "#2fbbac";
  }

  function highlightReturnLine(index) {
    const targetRow = getReturnLineTarget(index);
    targetRow.style.backgroundColor = "#b0d3f0";
  }

  function resetBook(index) {
    const targetRow = getBookTarget(index);
    targetRow.style.backgroundColor = "white";
  }

  function resetReturnLine(index) {
    const targetRow = getReturnLineTarget(index);
    targetRow.style.backgroundColor = "#e6eff7";
  }

  async function getLogisticsPackageById() {
    setLoading(true);
    const resp = await getLogisticsPackageByIdParams(id);
    setLoading(false);
    if (resp.success) {
      let pack = resp.data.list[0];
      setLogisticsPackage(pack);
      setLinesColumns([
        {
          name: "id",
          headerCaption: t("BookLinked"),
          textAlign: "center",
          size: "small",
          caption: (x) => {
            const icon =
              hasValue(x.barcodeBook) || hasValue(x.barcodeReturnLine) ? (
                <span className="icon checked">check_circle</span>
              ) : (
                <span className="icon">radio_button_unchecked</span>
              );

            const index =
              hasValue(x.barcodeBook)
                ? getBookFromPackage(x, pack)
                : (hasValue(x.barcodeReturnLine) ? getReturnLineFromPackage(x, pack) : -1);

            return (
              <span
                className="book-linked-wrapper"
                title={index !== -1 ? t("LineLinked") : t("LineNotLinked")}
              >
                {icon}
                {index !== -1 ? `#${index + 1}` : ``}
              </span>
            );
          },
        },
        {
          name: "sku",
          headerCaption: t("Sku"),
        },
        {
          name: "defective",
          headerCaption: t("Defective"),
          size: "small",
          textAlign: "center",
          caption: (x) =>
            x.defective ? (
              <div className="icon">check_box</div>
            ) : (
              <div className="icon">check_box_outline_blank</div>
            ),
        },
        {
          name: "quantity",
          textAlign: "right",
          headerCaption: t("Quantity"),
          size: "small",
        },
        {
          name: "action",
          headerCaption: "",
          size: "small",
          textAlign: "center",
          caption: (x) => {
            return (
              <div className="mapped-wrapper">
                <div
                  title={
                    x.isManuallyMapped
                      ? t("ThisLineIsManuallyMapped")
                      : t("ThisLineIsAutomaticallyMapped")
                  }
                  className={`mapped-type ${x.isManuallyMapped
                    ? "manual"
                    : x.isDuplicated
                      ? "duplicated"
                      : hasValue(x.barcodeBook)
                        ? "auto"
                        : ""
                    }`}
                >
                  {x.isManuallyMapped
                    ? t("Manual")
                    : x.isDuplicated
                      ? t("Duplicated")
                      : hasValue(x.barcodeBook)
                        ? t("Auto")
                        : ""}
                </div>
                {/* {(x.isManuallyMapped || !hasValue(x.barcodeBook)) && (
                              <div
                                className="icon pointer"
                                title={t("Map")}
                                onClick={() => toggleLinkToBookModal(x)}
                              >
                                sync_alt
                              </div>
                            )} */}
              </div>
            );
          },
        },
      ]);
    }
  }

  function compare(a, b) {
    var aint = parseInt(a.book.sku.split("-")[0]);
    var bint = parseInt(b.book.sku.split("-")[0]);
    if (aint < bint) return 1;
    if (aint > bint) return -1;
    return 0;
  }

  function compareLines(a, b) {
    var aint = parseInt(a.uid.split("-")[1]);
    var bint = parseInt(b.uid.split("-")[1]);
    if (aint < bint) return -1;
    if (aint > bint) return 1;
    return 0;
  }

  //#endregion

  return (
    <div className="logistics-package-page">
      {loading ? (
        <div className="loader-container">
          <div className="loader" />
        </div>) : (
        <>
          <div className="header-wrapper">
            <div
              className="goback outlined"
              onClick={() => navigate(-1)}
              role="link"
              id={"goback-button"}
            >
              <div className="text">{t("GoBack")}</div>
            </div>
            <div className="confirm-wrapper">
              {logisticsPackage.isMappedSaved ? (
                logisticsPackage.barcode &&
                !logisticsPackage.isMappedExported && (
                  <React.Fragment>
                    <div className="confirm-message">{t("MapIsSavedMessage")} | {t("Date")}: {logisticsPackage.isMappedSavedDate ? displayDateTime(logisticsPackage.isMappedSavedDate) : "-"}</div>
                    <Button
                      className="button"
                      onClick={() => setShowCompleteModal(true)}
                    >
                      {t("Edit")}
                    </Button>
                  </React.Fragment>
                )
              ) : (
                <React.Fragment>
                  {logisticsPackage.barcode &&
                    logisticsPackage.lines.filter((x) => !hasValue(x.barcodeBook))
                      .length > 0 && (
                      <Button
                        className="button"
                        onClick={() => setShowDuplicateModal(true)}
                      >
                        {t("CreateNewReceip")}
                      </Button>
                    )}
                  {logisticsPackage.barcodeReturn &&
                    logisticsPackage.lines.filter((x) => !hasValue(x.barcodeReturnLine))
                      .length > 0 && (
                      <Button
                        className="button"
                        onClick={() => setShowDuplicateModal(true)}
                      >
                        {t("CreateNewReceip")}
                      </Button>
                    )}
                  {logisticsPackage.barcode || logisticsPackage.barcodeReturn ? (
                    // COMPLETE
                    <Button
                      className="button"
                      onClick={() => setShowCompleteModal(true)}
                    >
                      {t("Complete")}
                    </Button>
                  ) : (
                    <React.Fragment>
                      {/* // MARK/UNMARK AS DISABLED */}
                      <Button
                        className="button"
                        onClick={() => setShowDisableModal(true)}
                      >
                        {logisticsPackage.disabled
                          ? t("UnmarkAsDisabled")
                          : t("MarkAsDisabled")}
                      </Button>
                      {/* // CHANGE DOMAIN */}
                      <Button
                        className="button"
                        onClick={handleChangeReturnStatusOnClick}
                      >
                        {t("ChangeReturnStatus")}
                      </Button>
                    </React.Fragment>
                  )}
                </React.Fragment>
              )}
            </div>
          </div >
          <div className="main-columns-wrapper">
            <div className="column">
              <div className="title-wrapper">
                <h2 className="title">{t("LogisticsInformation")}</h2>
              </div>
              <div className={`info-wrapper ${viewMoreInfoLog && `view-all`}`}>
                {logisticsInfo.map((x) => renderInfoRow(x, "logistics"))}
              </div>
              <div
                className="view-more-wrapper"
                onClick={() => setViewMoreInfoLog(!viewMoreInfoLog)}
              >
                <span className="icon">
                  {viewMoreInfoLog ? "keyboard_arrow_up" : "keyboard_arrow_down"}
                </span>
                <span className="text">
                  {viewMoreInfoLog ? t("ViewLess") : t("ViewMore")}
                </span>
              </div>
              <div className="lines-wrapper">
                <div className="subtitle-wrapper">
                  <h3 className="subtitle">
                    {`${t("Lines")} (${logisticsPackage.lines.length})`}
                  </h3>
                  {showLinesWarning && (
                    <div
                      className="icon"
                      title={showWarning ? t("HideWarning") : t("ShowWarning")}
                      onClick={() => setShowWarning(!showWarning)}
                    >
                      warning
                    </div>
                  )}
                </div>
                {showWarning && showLinesWarning && (
                  <div className="lines-warning">
                    <div className="icon">warning</div>
                    <div className="text">{t("WarningLinesNotLinked")}</div>
                  </div>
                )}
                <BasicTable
                  columns={linesColumns}
                  ref={tableLinesRef}
                  datasource={logisticsPackage.lines}
                  onRowOver={handleOnLineOver}
                  onRowOut={handleOnLineOut}
                />
              </div>
            </div>

            {/* RENDER NORMAL BARCODE INFO, RETURN INFO OR LINK SELECT BUTTONS */}
            {logisticsPackage.barcode || logisticsPackage.barcodeReturn ? (
              renderBarcodePackageInformation()
            ) : (
              <div className="column link-to-barcode">
                <h3 className="subtitle">{t("LogisticPackageNotLinked")}</h3>
                <div className="text">{t("LogisticPackageNotLinkedText")}</div>
                {logisticsPackage.hasReturnMark ? (
                  <Button
                    className="button"
                    onClick={handleLinkToReturnButtonClick}
                  >
                    {t("SelectReturn")}
                  </Button>
                ) : (
                  <Button
                    className="button"
                    onClick={handleLinkToNotReturnButtonClick}
                  >
                    {t("Select")}
                  </Button>
                )}
              </div>
            )}
          </div>
        </>
      )
      }
      {/* MODALS */}
      {/* ------ */}
      {
        showLinkToBarcodeModal && (
          <LinkToBarcodeModal
            show={showLinkToBarcodeModal}
            logisticsPackage={logisticsPackage}
            onHide={() => setShowLinkToBarcodeModal(false)}
            submit={handleLinkBarcodeModalSubmit}
          />
        )
      }
      {
        showLinkToReturnModal && (
          <LinkToReturnModal
            show={showLinkToReturnModal}
            logisticsPackage={logisticsPackage}
            line={activeLine}
            onHide={() => setShowLinkToReturnModal(false)}
            submit={handleLinkReturnModalSubmit}
          />
        )
      }
      {
        showLinkToBookModal && (
          <LinkToBookModal
            show={showLinkToBookModal}
            logisticsPackage={logisticsPackage}
            line={activeLine}
            onHide={() => setShowLinkToBookModal(false)}
            submit={handleLinkToBookModalSubmit}
          />
        )
      }
      {
        showCompleteModal && (
          <CompleteModal
            show={showCompleteModal}
            isMappedSaved={logisticsPackage.isMappedSaved}
            onHide={() => setShowCompleteModal(false)}
            complete={() => {
              handleCompleteModalComplete();
            }}
          />
        )
      }
      {
        showDisableModal && (
          <DisableReceipModal
            show={showDisableModal}
            isMappedSaved={logisticsPackage.isMappedSaved}
            onHide={() => setShowDisableModal(false)}
            disable={handleDisableReceipModalDisable}
          />
        )
      }
      {
        showChangeReturnStatusModal && (
          <ChangeReturnStatusModal
            show={showChangeReturnStatusModal}
            onConfirm={handleChangeReturnStatusOnConfirm}
            onHide={() => setShowChangeReturnStatusModal(false)} />
        )
      }
      {
        showDuplicateModal && (
          <DuplicateModal
            show={showDuplicateModal}
            onHide={() => setShowDuplicateModal(false)}
            duplicate={() => {
              handleDuplicateModalDup();
            }}
          />
        )
      }
    </div >
  );
}
